import { ArrowRightOutlined } from '@ant-design/icons';
import { Badge } from 'components/ui/badge';
import dayjs from 'dayjs';
import { useIsFirstLineCaregiver } from 'hooks/useCaregiverMe';
import { CaregiverIdentity, useCaregiverIdentity } from 'hooks/useCaregivers';
import { getLatestActivityDate } from 'hooks/useConversations';
import useMarkConversationAsRead from 'hooks/useMarkConversationAsRead';
import useSelectConversations from 'hooks/useSelectConversations';
import { cn } from 'lib/utils';
import posthog from 'posthog-js';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { routes } from 'services/RouteService';
import { ConversationDTO } from 'services/api/models/ConversationDTO';
import { UserDTO } from 'services/api/models/UserDTO';
import { selectCurrentUser } from 'store/users/selectors';
import ChatItem from './ChatItem';
import PatientAccessTierIcon from './PatientAccessTierIcon';

export type ChatsListProps = {
  conversations: ConversationDTO[];
  title: string | null;
  readOnlyConvs: boolean;
  maximumConversations?: number;
  /**
   * The messaging redesign's patient list: her referring caregiver, who also
   * shows on the conversations no caregiver has taken yet, by first name and
   * marked « votre référente »; the others by name and specialty.
   */
  referent?: CaregiverIdentity;
  /** Who a patient's conversation shows until a caregiver takes it (default « Équipe Gynger »). */
  unassignedRecipient?: { name: string; photoUrl?: string };
};

/** A caregiver in the patient's list, named as the redesign does (Figma « Conversation », 262:17097). */
function CaregiverChatTitle({ owner, referent }: { owner?: UserDTO; referent: CaregiverIdentity }) {
  const { t } = useTranslation();
  const caregiver = useCaregiverIdentity(owner);
  if (!caregiver || caregiver.id === referent.id) {
    return (
      <span className="flex min-w-0 items-center gap-2">
        <span className="truncate">{(caregiver ?? referent).firstName}</span>
        <Badge variant="secondary">{t('messagingV3.list.referentBadge')}</Badge>
      </span>
    );
  }
  return <>{[caregiver.name, caregiver.role?.toLocaleLowerCase('fr')].filter(Boolean).join(' - ')}</>;
}

/** The tones of the old `Tag`, mapped to `Badge` variants. */
type StatusTone = 'accent' | 'warning' | 'error';
const STATUS_TONE_VARIANT: Record<StatusTone, 'success' | 'destructive-muted' | 'destructive'> = {
  accent: 'success',
  warning: 'destructive-muted',
  error: 'destructive',
};

const chatsListBaseClassName = 'block overflow-auto';

export default function ChatsList(props: ChatsListProps) {
  const { conversations, title, readOnlyConvs, maximumConversations, referent, unassignedRecipient } = props;

  const displayedConversations = maximumConversations ? conversations.slice(0, maximumConversations) : conversations;

  // Hooks
  const navigate = useNavigate();
  const { t } = useTranslation();
  const markConversationAsRead = useMarkConversationAsRead();

  // Selectors
  const { conversationId: mainConversationId } = useSelectConversations();
  const currentUser = useSelector(selectCurrentUser);
  const isFirstLineCaregiver = useIsFirstLineCaregiver();

  async function onChatClicked(conversation: Pick<ConversationDTO, 'id'>) {
    if (!readOnlyConvs) {
      navigate(routes.conversation.route.replace(':conversationId', conversation.id.toString()));

      await markConversationAsRead(conversation.id.toString());
      posthog.capture('chats.conversation_opened');
    } else {
      navigate(
        routes.patientHistoricConversation.route
          .replace(':conversationId', mainConversationId)
          .replace(':historicConversation', conversation.id.toString()),
      );
    }
  }
  const convertConversations = (conversationsList: ConversationDTO[]) => {
    return conversationsList.map(conversation => {
      // If the current user is a caregiver, display the patient avatar or a failover avatar. Otherwise, display the caregiver's avatar
      const recipientAvatar = currentUser?.is_caregiver
        ? conversation?.patient.photo_url || undefined
        : conversation?.current_owner
          ? conversation.current_owner.photo_url || undefined
          : unassignedRecipient?.photoUrl;

      // If the current user is a caregiver, display the patient name or a failover name. Otherwise, display the caregiver's name
      const recipientName =
        currentUser?.is_caregiver && !readOnlyConvs
          ? conversation?.patient?.display_name || t('chats.noCaregiver.name.forCaregiver')
          : conversation?.current_owner?.display_name ||
            unassignedRecipient?.name ||
            t('chats.noCaregiver.name.forPatient');

      const patientAccessTier =
        currentUser?.is_caregiver && !readOnlyConvs ? conversation.patient?.access_tier : undefined;

      const unreadValue: number = conversation.number_of_unread_messages;

      // Determine the waiting label for "En attente" conversations
      // Only show label for conversations waiting for assignment (owner is NOT current user)
      let statusText: string | undefined;
      let statusTone: StatusTone | undefined;

      if (currentUser?.is_caregiver && conversation.status && !readOnlyConvs) {
        const { status, current_owner } = conversation;
        // Only show label for "En attente" conversations (not "En redirection")
        // This means the owner is NOT the current user
        if (current_owner?.id !== currentUser.id) {
          if (status.value === 'ASSIGNMENT_TO_GROUP_ASKED' && status.group) {
            // Waiting for a group - show the group name
            statusText = t('chats.caregiverStatus.new');
          } else if (status.value === 'ASSIGNMENT_TO_CAREGIVER_ASKED') {
            // Waiting for a specific caregiver - show "me"
            statusText = t('chats.caregiverStatus.waitingForMe');
            statusTone = 'accent';
          } else if (
            status.value === 'WAITING_FOR_FIRST_RESPONSE' ||
            status.value === 'FIRST_ASSIGNMENT_REJECTED' ||
            status.value === 'FIRST_ASSIGNMENT_TIMED_OUT'
          ) {
            statusText = t('chats.caregiverStatus.new');
          }
        } else {
          // my conversations
          const lastMessage = conversation.last_message;
          if (status.value === 'ASSIGNED_TO_CAREGIVER' && lastMessage?.sender?.id !== currentUser.id) {
            const messageDelay = dayjs().diff(dayjs(getLatestActivityDate(conversation)), 'hours');
            if (messageDelay >= 16) {
              statusText = t('chats.caregiverStatus.inNeedOfAttention');
            }
          }
        }
      }
      const lastMessage = conversation.last_message;
      if (currentUser?.is_caregiver && statusText && lastMessage?.sender?.id !== currentUser?.id) {
        const messageDelay = dayjs().diff(dayjs(getLatestActivityDate(conversation)), 'hours');
        if (messageDelay >= 40) {
          statusTone = 'error';
        } else if (messageDelay >= 16) {
          statusTone = 'warning';
        }
      }
      if (readOnlyConvs) {
        statusText =
          conversation.status?.value !== 'ASSIGNED_TO_CAREGIVER'
            ? t('chats.statuses.' + conversation.status?.value)
            : undefined;
        switch (conversation.status?.value) {
          case 'ASSIGNMENT_TO_GROUP_ASKED':
            statusTone = 'warning';
            break;
          case 'ASSIGNMENT_TO_CAREGIVER_ASKED':
            statusTone = 'warning';
            break;
          case 'WAITING_FOR_FIRST_RESPONSE':
          case 'FIRST_ASSIGNMENT_REJECTED':
          case 'FIRST_ASSIGNMENT_TIMED_OUT':
            statusTone = 'warning';
            break;
          case 'ASSIGNED_TO_CAREGIVER':
            statusTone = 'accent';
            break;
          case 'CLOSED':
            statusTone = 'accent';
            break;
        }
      }

      const wasLastMessageSentByCurrentUser = conversation.last_message?.sender?.id === currentUser?.id;
      const showQuestionnaireTag = isFirstLineCaregiver && conversation.questionnaire_needs_review && !readOnlyConvs;

      return {
        ...conversation,
        avatar: recipientAvatar,
        title: recipientName,
        patientAccessTier,
        subtitle: (
          <>
            {wasLastMessageSentByCurrentUser && <ArrowRightOutlined className="mr-1 text-subtle-foreground" />}
            {showQuestionnaireTag && <Badge variant="warning">{t('medicalProfile.caregiver.needsReviewTag')}</Badge>}
            <span className="truncate">
              {conversation.last_message?.is_deleted ? (
                <em>{t('chats.deletedLastMessage')}</em>
              ) : (
                conversation.last_message?.message_text || t('chats.noLastMessage')
              )}
            </span>
          </>
        ),
        date: getLatestActivityDate(conversation) ?? new Date(),
        unread: unreadValue,
        statusText,
        statusTone,
      };
    });
  };

  return (
    <>
      {title && <h3 className="pl-3">{title}</h3>}

      {displayedConversations.length === 0 ? (
        <p className="text-center whitespace-pre-line text-muted-foreground">
          {' '}
          {currentUser?.is_caregiver ? t('chats.list.noChats.forCaregiver') : t('chats.list.noChats.forPatient')}
        </p>
      ) : (
        <div
          className={cn(
            chatsListBaseClassName,
            currentUser?.is_caregiver
              ? 'min-h-[30%]'
              : // About 4 conversations, fading out at the bottom (a mask reads only
                // the alpha channel, so `black` here means "fully opaque").
                'mb-2 max-h-72 mask-b-from-80%',
          )}
        >
          {convertConversations(displayedConversations).map(c => {
            return (
              <ChatItem
                key={c.id}
                avatar={c.avatar}
                title={
                  c.patientAccessTier ? (
                    <span className="inline-flex items-center gap-1.5">
                      <PatientAccessTierIcon accessTier={c.patientAccessTier} className="shrink-0 text-sm" />
                      {c.title}
                    </span>
                  ) : referent ? (
                    <CaregiverChatTitle owner={c.current_owner} referent={referent} />
                  ) : (
                    c.title
                  )
                }
                subtitle={
                  <>
                    {c.statusText && (
                      <Badge variant={c.statusTone ? STATUS_TONE_VARIANT[c.statusTone] : 'secondary'}>
                        {c.statusText}
                      </Badge>
                    )}
                    {c.subtitle}
                  </>
                }
                dateString={dayjs(c.date).fromNow()}
                unread={c.unread}
                onClick={() => onChatClicked(c)}
              />
            );
          })}
          {displayedConversations.length >= 4 && <div className="h-5" />}
        </div>
      )}
    </>
  );
}
